import type { ColumnDef } from '@tanstack/react-table'
import { ArrowUpCircle } from 'lucide-react'
import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { AddClientModal } from '@/components/admin/coaching/add-client-modal'
import type { ClientFormData } from '@/components/admin/problems/types'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { Input } from '@/components/ui/input'
import { SimpleSelect } from '@/components/ui/simple-select'
import { trpc } from '@/lib/trpc'
import { type Lifecycle, LifecycleBadge } from './lifecycle'

const PAGE_SIZE = 25

interface UserRow {
  id: string
  email: string | null
  name: string | null
  role: string | null
  lifecycle: Lifecycle
  createdAt: number
  lastSignInAt: number | null
}

function formatDate(epochSeconds: number | null): string {
  if (epochSeconds === null) return '—'
  return new Date(epochSeconds * 1000).toLocaleDateString()
}

/**
 * The "All Users" tab on the coaching page: a roster of every platform user
 * with the ability to promote a freemium user into a direct coaching client.
 * Promotion opens the same Add Client modal (pre-filled from the user) and
 * creates a linked CoachingClient — so the promoted user immediately appears in
 * the Direct-Coach Clients tab and gains the `client` role.
 */
export function AllUsersTab() {
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [lifecycleFilter, setLifecycleFilter] = useState<Lifecycle | 'all'>(
    'all',
  )
  const [promoteUser, setPromoteUser] = useState<UserRow | null>(null)
  const [demoteTarget, setDemoteTarget] = useState<UserRow | null>(null)

  const utils = trpc.useUtils()
  const listQuery = trpc.admin.users.list.useQuery({
    query: search.trim() || undefined,
    limit: PAGE_SIZE,
    offset,
  })

  const createMutation = trpc.admin.clients.create.useMutation({
    onSuccess: () => {
      utils.admin.clients.list.invalidate()
      utils.admin.users.list.invalidate()
      toast.success('Promoted to direct client')
      setPromoteUser(null)
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to promote user')
    },
  })

  const demoteMutation = trpc.admin.clients.demoteUser.useMutation({
    onSuccess: () => {
      utils.admin.clients.list.invalidate()
      utils.admin.users.list.invalidate()
      toast.success('Demoted to freemium')
      setDemoteTarget(null)
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to demote user')
    },
  })

  const promoteInitialData = useMemo<Partial<ClientFormData> | undefined>(
    () =>
      promoteUser
        ? {
            name: promoteUser.name ?? '',
            email: promoteUser.email ?? '',
            // Pre-supplying userId hides AddClientModal's platform-user
            // picker: the promoted user IS the platform user, so making an
            // admin re-search for them would be redundant.
            userId: promoteUser.id,
          }
        : undefined,
    [promoteUser],
  )

  const columns = useMemo<ColumnDef<UserRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => row.original.name ?? '—',
      },
      { accessorKey: 'email', header: 'Email' },
      {
        accessorKey: 'lifecycle',
        header: 'Lifecycle',
        cell: ({ row }) => (
          <LifecycleBadge lifecycle={row.original.lifecycle} />
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        accessorKey: 'lastSignInAt',
        header: 'Last sign-in',
        cell: ({ row }) => formatDate(row.original.lastSignInAt),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const u = row.original
          if (u.lifecycle === 'freemium') {
            return (
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setPromoteUser(u)}>
                  <ArrowUpCircle size={14} className="mr-1" />
                  Promote
                </Button>
              </div>
            )
          }
          if (u.lifecycle === 'direct_client') {
            return (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDemoteTarget(u)}
                >
                  Demote
                </Button>
              </div>
            )
          }
          return null
        },
      },
    ],
    [],
  )

  const rows = (listQuery.data?.users ?? []) as UserRow[]
  const visibleRows =
    lifecycleFilter === 'all'
      ? rows
      : rows.filter((u) => u.lifecycle === lifecycleFilter)

  const totalCount = listQuery.data?.totalCount ?? 0
  const pageStart = totalCount === 0 ? 0 : offset + 1
  const pageEnd = Math.min(offset + PAGE_SIZE, totalCount)

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input
          value={search}
          placeholder="Search by name or email…"
          className="max-w-xs"
          onChange={(e) => {
            setSearch(e.target.value)
            setOffset(0)
          }}
        />
        <SimpleSelect
          value={lifecycleFilter}
          onChange={(v) => setLifecycleFilter(v as Lifecycle | 'all')}
          options={[
            { value: 'all', label: 'All lifecycles' },
            { value: 'freemium', label: 'Freemium' },
            { value: 'direct_client', label: 'Direct clients' },
            { value: 'admin', label: 'Admins' },
          ]}
          width="md"
        />
      </div>

      {listQuery.isError ? (
        <p className="text-destructive">
          Failed to load users: {listQuery.error.message}
        </p>
      ) : (
        <DataTable
          columns={columns}
          data={visibleRows}
          enablePagination={false}
          enableColumnVisibility={false}
        />
      )}

      {/* Server-side pagination (Clerk-backed). The lifecycle filter applies to
          the current page only, so counts reflect the unfiltered page. */}
      <div className="mt-4 flex items-center justify-between">
        <span className="text-muted-foreground text-sm">
          {listQuery.isPending
            ? 'Loading…'
            : `Showing ${pageStart}–${pageEnd} of ${totalCount}`}
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0 || listQuery.isPending}
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={pageEnd >= totalCount || listQuery.isPending}
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Promote = Add Client, pre-filled from the platform user + linked by id. */}
      <AddClientModal
        isOpen={promoteUser !== null}
        onClose={() => setPromoteUser(null)}
        title="Promote to Direct Client"
        initialData={promoteInitialData}
        onSubmit={async (data) => {
          if (!promoteUser) return
          const { status, ...rest } = data
          await createMutation.mutateAsync({
            ...rest,
            status: status as 'active' | 'paused' | 'archived' | undefined,
            userId: promoteUser.id,
          })
        }}
      />

      {/* Demote = "nuclear" option: clears client access AND archives the
          coaching record. Heavily confirmed. */}
      <AlertDialog
        open={demoteTarget !== null}
        onOpenChange={(o) => !o && setDemoteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Demote {demoteTarget?.name || demoteTarget?.email} to freemium?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This is a destructive action. It will revoke their client access
              (client-only problems), and{' '}
              <strong>archive their coaching record</strong> (homework,
              check-ins and history). The coaching record can only be restored
              by an admin. This cannot be undone from here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={demoteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              disabled={demoteMutation.isPending}
              onClick={(e) => {
                // Keep the dialog mounted until the mutation settles.
                e.preventDefault()
                if (demoteTarget) {
                  demoteMutation.mutate({ userId: demoteTarget.id })
                }
              }}
            >
              {demoteMutation.isPending ? 'Demoting…' : 'Demote & archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
